<template>
  <div class="scenario">
    <div class="scenario__main">
      <div class="scenario__chessboard" :style="boardStyle">
        <Chessboard
          ref="chessboard"
          :orientation="boardOrientation"
          :initialFen="currentFen"
          :viewOnly="false"
        />
        <BoardControls
          class="board-controls"
          :show-board-size="!isPresentationMode"
          :show-flip="true"
          :show-fen-copy="true"
          :show-clear="true"
          :show-reset="true"
          :show-scenario="fenData && fenData.isValid"
          :show-exit="true"
          :fen-value="currentFen"
          @size-changed="handleBoardSizeChange"
          @flip="flipBoard"
          @clear="clearBoard"
          @reset="resetBoard"
          @scenario="loadScenarioFromFen"
          @exit="exitFreeMode"
        />
      </div>

      <div class="scenario__moves" :style="movesTableStyle">
        <!-- PieceSelector component -->
        <PieceSelector
          @pieceSelected="handlePieceSelected"
          @eraserSelected="handleEraserSelected"
        />

        <!-- New BoardSettings component -->
        <BoardSettings :fen="currentFen" @fenUpdated="currentFen = $event" />

        <div class="scenario__status">
          <span>{{ currentFen | wrappableFen }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  CHESS_START_POSITION,
  CHESS_EMPTY_BOARD_FEN,
} from "vue-chess-shared/constants";
import { FenData } from "vue-chess-shared/pgn/FenData";
import Chess from "chess.js";
import Chessboard from "@/components/games/Chessboard";
import PieceSelector from "@/components/training/scenarios/PieceSelector";
import BoardSettings from "@/components/training/scenarios/BoardSettings";
import BoardControls from "./BoardControls.vue";
import { boardSizeMixin } from "@/mixins/boardSizeMixin";

export default {
  name: "ScenarioFree",

  mixins: [boardSizeMixin],

  components: {
    Chessboard,
    PieceSelector,
    BoardSettings,
    BoardControls,
  },

  props: {
    fen: {
      type: String,
      required: true,
    },
  },

  computed: {
    fenData() {
      return new FenData(this.currentFen, this.chess);
    },
  },

  data() {
    return {
      chess: new Chess(),
      currentFen: "",
      selectedPiece: null,
    };
  },

  watch: {
    fen: {
      immediate: true,
      handler(newFen) {
        this.currentFen = newFen;
      },
    },

    currentFen: {
      immediate: true,
      handler(newFen) {
        this.$nextTick(() => {
          this.renderFen(newFen);
        });
      },
    },
  },

  methods: {
    exitFreeMode() {
      this.$emit("exitFreeMode");
    },

    clearBoard() {
      this.currentFen = CHESS_EMPTY_BOARD_FEN;
    },

    resetBoard() {
      this.currentFen = CHESS_START_POSITION;
    },

    loadScenarioFromFen() {
      this.$emit("loadScenarioFromFen", this.currentFen);
    },

    handlePieceSelected(piece) {
      this.selectedPiece = piece;
    },

    handleEraserSelected() {
      this.selectedPiece = "X";
    },

    getChessground() {
      if (!this.$refs.chessboard) {
        return;
      }
      return this.$refs.chessboard.ui;
    },

    updateFen() {
      const cgFen = this.getChessground()?.getFen();
      this.fenData.piecePlacement = cgFen;
      this.currentFen = this.fenData.fen;
    },

    renderFen(fen) {
      this.getChessground()?.set({
        fen,
        movable: {
          color: "both",
          free: true,
          events: {
            after: () => {
              this.updateFen();
            },
          },
        },
        events: {
          select: this.handleClick,
        },
      });
    },

    handleClick(square) {
      if (this.selectedPiece === null) return; // If no piece is selected, do nothing

      const cg = this.getChessground();

      if (this.selectedPiece === "X") {
        cg.setPieces({
          [square]: null,
        });
      } else if (!cg.state.pieces[square]) {
        cg.newPiece(this.selectedPiece, square);
      }

      this.updateFen();
    },
  },
};
</script>

<style scoped>
@import "../../../assets/styles/scenario-board.css";
</style>
